// src/pages/finance/subledger/AddSubLedgerEntry.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import Select from "react-select";

function AddSubLedgerEntry() {

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const navigate = useNavigate();
  const routeLocation = useLocation();

  const editData = routeLocation?.state?.SubLedgerData;

  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);

  const [form, setForm] = useState({
    party_id: 0,
    account_head_id: "",
    party_nature: "",
    party_name: "",
    pan: "",
    aadhaar: "",
    amount: "",
    notes: "",
  });

  useEffect(() => {
    if (editData) {
      setForm({
        party_id: editData.party_id || 0,
        account_head_id: editData.account_head_id || "",
        party_nature: editData.party_nature || "",
        party_name: editData.party_name || "",
        pan: editData.pan || "",
        aadhaar: editData.aadhaar || "",
        amount: editData.amount || "",
        notes: editData.notes || "",
      });

      setEditId(editData.party_id);
    }
  }, [editData]);

  const fetchAccounts = async () => {
    try {
      setIsAccountsLoading(true);

      const resp = await axios.get(`${baseUrl}/subledger-accounts/`, {
        params: { school_id: userObj.school_id },
      });

      setAccounts(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
    } finally {
      setIsAccountsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const accountOptions = useMemo(() => {
    return (accounts || []).map((x) => ({
      value: x.account_head_id,
      label: x.account_head_name,
    }));
  }, [accounts]);

  const selectedAccount = useMemo(() => {
    if (!form.account_head_id) return null;
    return accountOptions.find(
      (o) => String(o.value) === String(form.account_head_id)
    );
  }, [form.account_head_id, accountOptions]);

  const partyNatureOptions = [
    { value: "Individual", label: "Individual" },
    { value: "Company", label: "Company" },
    { value: "Firm", label: "Firm" },
    { value: "Other", label: "Other" },
  ];

  const validate = () => {
    if (!form.account_head_id) return "Account is required";
    if (!form.party_nature) return "Party Nature is required";
    if (!form.party_name) return "Party Name is required";
    if (!form.amount) return "Amount is required";
    if (Number(form.amount) <= 0) return "Amount must be greater than 0";
    return null;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) return toast.error(err);

    try {
      setIsSaving(true);

      const payload = {
        action: editId ? "UPDATE" : "CREATE",
        party_id: editId || 0,
        school_id: userObj.school_id,
        account_head_id: Number(form.account_head_id),
        party_nature: form.party_nature,
        party_name: form.party_name,
        pan: form.pan || null,
        aadhaar: form.aadhaar || null,
        amount: form.amount ? Number(form.amount) : null,
        notes: form.notes || null,
        created_by: userObj.username || "admin",
        modified_by: userObj.username || "admin",
      };

      const resp = await axios.post(
        `${baseUrl}/subledger-party-details/`,
        payload
      );

      // ✅ handle backend response
      if (resp.data?.status === "error") {
        toast.error(resp.data.message || "Operation failed");
        return;
      }

      toast.success(
        editId
          ? "Subledger Updated Successfully"
          : "Subledger Saved Successfully"
      );

      handleReset();

    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Server Error"
      );
    } finally {
      setIsSaving(false);
    }
  };
  const handleReset = () => {
    setEditId(null);

    setForm({
      party_id: 0,
      account_head_id: "",
      party_nature: "",
      party_name: "",
      pan: "",
      aadhaar: "",
      amount: "",
      notes: "",
    });
  };

  return (
    <div className="pageBody">

      <ToastContainer />

      <Container fluid>

        <Card>

          <Card.Body>

            <form onSubmit={handleSubmit}>

              <Row>

                <Col xs={12}>
                  <h6 className="commonSectionTitle">
                    {editId ? "Edit SubLedger Entry" : "Subledger Entry Details"}
                  </h6>
                </Col>

              </Row>

              <Row>

                {/* Account */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">

                    <Form.Group>

                      <Form.Label>
                        Account <span className="requiredStar">*</span>
                      </Form.Label>

                      <Select
                        placeholder="Select Account"
                        options={accountOptions}
                        isLoading={isAccountsLoading}
                        value={selectedAccount}
                        onChange={(opt) =>
                          setForm((f) => ({
                            ...f,
                            account_head_id: opt ? opt.value : "",
                          }))
                        }
                      />

                    </Form.Group>

                  </div>
                </Col>

                {/* Party Nature */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">

                    <Form.Group>

                      <Form.Label>
                        Select Party Nature <span className="requiredStar">*</span>
                      </Form.Label>

                      <Select
                        options={partyNatureOptions}
                        placeholder="Select Party Nature"
                        isClearable
                        value={
                          partyNatureOptions.find((o) => o.value === form.party_nature) || null
                        }
                        onChange={(opt) =>
                          setForm((f) => ({
                            ...f,
                            party_nature: opt ? opt.value : "",
                          }))
                        }
                      />

                    </Form.Group>

                  </div>
                </Col>

                {/* Party Name */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">

                    <Form.Group>

                      <Form.Label>
                        Party Name <span className="requiredStar">*</span>
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Enter Party Name"
                        value={form.party_name}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            party_name: e.target.value,
                          }))
                        }
                      />

                    </Form.Group>

                  </div>
                </Col>

                {/* PAN */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">

                    <Form.Group>

                      <Form.Label>PAN</Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Enter PAN"
                        value={form.pan}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            pan: e.target.value,
                          }))
                        }
                      />

                    </Form.Group>

                  </div>
                </Col>

                {/* Aadhaar */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">

                    <Form.Group>

                      <Form.Label>Aadhaar</Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Enter Aadhaar"
                        value={form.aadhaar}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            aadhaar: e.target.value,
                          }))
                        }
                      />

                    </Form.Group>

                  </div>
                </Col>

                {/* Amount */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">

                    <Form.Group>

                      <Form.Label>Amount <span className="requiredStar">*</span></Form.Label>

                      <Form.Control
                        type="number"
                        placeholder="Enter Amount"
                        value={form.amount}
                        min="0"
                        step="0.01"
                        onKeyDown={(e) => {
                          if (["-", "e", "E", "+"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (value === "") {
                            setForm((f) => ({
                              ...f,
                              amount: "",
                            }));
                            return;
                          }

                          if (/^\d*\.?\d*$/.test(value)) {
                            setForm((f) => ({
                              ...f,
                              amount: value,
                            }));
                          }
                        }}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData("text");
                          if (!/^\d*\.?\d*$/.test(pasted)) {
                            e.preventDefault();
                          }
                        }}
                      />

                    </Form.Group>

                  </div>
                </Col>

                {/* Notes */}
                <Col xs={12} md={12} lg={8} xxl={6}>
                  <div className="commonInput">

                    <Form.Group>

                      <Form.Label>Notes</Form.Label>

                      <Form.Control
                        as="textarea"
                        rows={1}
                        placeholder="Enter Notes"
                        value={form.notes}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            notes: e.target.value,
                          }))
                        }
                      />

                    </Form.Group>

                  </div>
                </Col>

              </Row>

              <div className="d-flex justify-content-between mt-3">

                <div>

                  <Button
                    type="button"
                    className="btn-info clearBtn"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>

                </div>

                <div>

                  <Button
                    type="button"
                    className="btn-danger secondaryBtn me-2"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    className="btn-success primaryBtn"
                  >
                    {editId ? "Update" : "Submit"}
                  </Button>

                </div>

              </div>

            </form>

          </Card.Body>

        </Card>

      </Container>

    </div>
  );
}

export default AddSubLedgerEntry;